import { GitCommitIcon } from '@langchain/untitled-ui-icons';

import { Link } from 'react-router-dom';

import RelativeTime from '@/components/Hub/RelativeTime';
import { useSetTab } from '@/components/Tabs/useTab';
import { useCommits, useOrganizationId } from '@/hooks/useSwr';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { useRepoData } from './hooks/useRepoData';

const CommitCircle = ({ active }: { active?: boolean }) => {
  return (
    <div
      className={cn(
        'relative flex h-5 w-5 items-center justify-center gap-2 rounded-[40px] bg-gradient-to-br text-white',
        active
          ? 'from-brand-green-500  to-cyan-400'
          : 'from-gray-600 to-gray-400'
      )}
    >
      <GitCommitIcon className="size-4 p-0.5" />
    </div>
  );
};

const RepoDetailCommits = ({ inHub }: { inHub: boolean }) => {
  const { owner, repo, commit, repoObj } = useRepoData(inHub);
  const currentCommit = commit || repoObj?.repo?.last_commit_hash || '';
  const { data: commits, isLoading: commitsLoading } = useCommits(
    owner,
    repo!,
    {
      limit: 4,
      offset: 0,
    }
  );
  const organizationId = useOrganizationId();
  const setTab = useSetTab({ method: 'merge' });
  return (
    <div className="flex flex-1 flex-col items-start justify-start gap-9 px-4 py-6">
      <div className="flex flex-col items-stretch justify-start gap-3 self-stretch">
        <div className="flex flex-col items-start justify-start gap-3">
          <div className="text-base font-medium leading-snug text-slate-900 dark:text-slate-100">
            Commits
          </div>
        </div>
        <div className="flex flex-col items-stretch justify-start gap-2">
          {!commitsLoading &&
            commits &&
            commits.commits
              .map((c) => ({
                ...c,
                active:
                  currentCommit.substring(0, 8) ===
                  c.commit_hash.substring(0, 8),
              }))
              .map((c, i) => (
                <Link
                  key={c.id}
                  to={addQueryParams(
                    `/${
                      inHub
                        ? appHubIndexPath + '/' + owner
                        : appPromptsIndexPath
                    }/${repo}/${c.commit_hash.substring(0, 8)}`,
                    {
                      organizationId,
                    }
                  )}
                >
                  <div className="flex flex-col items-stretch justify-start gap-3">
                    <div className="inline-flex items-center justify-start gap-1.5">
                      <div className="inline-flex flex-col items-center justify-start gap-1 self-stretch">
                        <CommitCircle active={c.active} />
                        {i !== commits.total - 1 && (
                          <div className="h-[20px] w-px rounded-sm bg-tertiary" />
                        )}
                      </div>
                      <div className="mb-auto flex shrink grow basis-0 flex-wrap items-baseline justify-between gap-1.5">
                        <div className="inline-flex flex-col items-start justify-start gap-3.5">
                          <div className="flex flex-col items-start justify-start gap-1">
                            <div className="text-xs font-medium leading-none text-slate-900 dark:text-slate-100">
                              {c.commit_hash.substring(0, 8)}
                            </div>
                            {c.full_name && (
                              <div className="mt-[2px] flex text-xs font-normal leading-none text-slate-900 dark:text-slate-100">
                                <span>
                                  by{' '}
                                  <span className="font-semibold">
                                    {c.full_name}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex h-4 items-start justify-end gap-1.5">
                          <div className="whitespace-nowrap text-xs font-normal leading-none text-slate-500 dark:text-slate-400">
                            <RelativeTime utcTime={c.created_at} shortFormat />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
        </div>

        {commits?.total && commits.total > 4 && (
          <div
            onClick={() => setTab(1)}
            className="cursor-pointer text-xs font-semibold leading-none text-brand-green-500"
          >
            See all {commits.total} commits
          </div>
        )}
      </div>
    </div>
  );
};

export default RepoDetailCommits;
